import { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Calendar, QrCode, Radio, Mail, BarChart3, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

const CONSENT_VERSION = "v1";

// Routes publiques / onboarding qui ne doivent PAS être bloquées par le consentement.
// Tout chemin commençant par un de ces préfixes est exempté.
const PUBLIC_PREFIXES = [
  "/auth",
  "/set-password",
  "/mentions-legales",
  "/confidentialite",
  "/cgu",
  "/cgv",
  "/contact",
  "/legal/",
  "/magasins",
  "/fournisseurs",
  "/onepage-magasin",
  "/checkin/",
  "/scan",
  "/external-visitor",
  "/visitor-checkin",
  "/centrale/visitor-checkin",
  "/qr/",
  "/s/",
  "/unsubscribe",
];

function isExemptPath(pathname: string): boolean {
  if (pathname === "/") return true;
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

function detectSource(pathname: string): string {
  if (pathname.startsWith("/scan") || pathname.startsWith("/checkin/") || pathname.startsWith("/qr") || pathname.startsWith("/s/")) {
    return "qr";
  }
  if (pathname.startsWith("/set-password")) return "invitation";
  return "login";
}

export function DataConsentGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, userProfile, refreshAuth } = useAuth();
  const location = useLocation();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const exempt = useMemo(() => isExemptPath(location.pathname), [location.pathname]);

  // Pas de blocage tant qu'on n'a pas l'info, ou si non connecté, ou si route publique.
  if (isLoading || !isAuthenticated || exempt) {
    return <>{children}</>;
  }

  // Profil pas encore résolu (ex: just après login) → laisser passer, RouteGuard gérera.
  if (!userProfile) {
    return <>{children}</>;
  }

  // Consentement déjà donné → passer.
  if (userProfile.data_consent_accepted_at) {
    return <>{children}</>;
  }

  const handleSubmit = async () => {
    if (!accepted || submitting) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc("record_data_consent", {
        _version: CONSENT_VERSION,
        _source: detectSource(location.pathname),
      });
      if (error) throw error;
      await refreshAuth();
    } catch (e: any) {
      console.error("[DataConsentGate] record_data_consent failed:", e);
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer votre consentement. Réessayez.",
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-background overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-2xl bg-card border border-border rounded-2xl shadow-lg p-6 sm:p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                Protection de vos données
              </h1>
              <p className="text-xs text-muted-foreground">
                Avant de continuer, merci de prendre connaissance des informations ci-dessous.
              </p>
            </div>
          </div>

          <div className="space-y-3 text-sm text-foreground/80 leading-relaxed">
            <p>
              Lyko traite certaines données personnelles strictement nécessaires au
              fonctionnement du service. Voici ce que cela couvre&nbsp;:
            </p>

            <ul className="space-y-2.5">
              <li className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span><strong>Rendez-vous</strong> : création, gestion et historique de vos rendez-vous fournisseurs/magasins.</span>
              </li>
              <li className="flex items-start gap-3">
                <QrCode className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span><strong>Scans QR</strong> : enregistrement de vos scans (check-in / check-out) en magasin ou centrale.</span>
              </li>
              <li className="flex items-start gap-3">
                <Radio className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span><strong>Présence</strong> : suivi de vos arrivées et départs sur site, à des fins de sécurité et de pilotage.</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span><strong>Emails</strong> : envoi des notifications transactionnelles (confirmations, rappels, alertes).</span>
              </li>
              <li className="flex items-start gap-3">
                <BarChart3 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span><strong>Analytics</strong> : statistiques agrégées et anonymisées pour mesurer l'usage du service.</span>
              </li>
            </ul>

            <p className="text-xs text-muted-foreground pt-2">
              Conformément au RGPD, vous disposez d'un droit d'accès, de rectification,
              d'opposition et de suppression de vos données. Plus d'informations dans
              notre{" "}
              <Link to="/legal/privacy" target="_blank" className="text-primary hover:underline">
                politique de confidentialité
              </Link>.
            </p>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/40 border border-border">
            <Checkbox
              id="data-consent-checkbox"
              checked={accepted}
              onCheckedChange={(v) => setAccepted(v === true)}
              disabled={submitting}
              className="mt-0.5"
            />
            <Label
              htmlFor="data-consent-checkbox"
              className="text-sm text-foreground cursor-pointer leading-relaxed"
            >
              J'ai lu et j'accepte le traitement de mes données par Lyko, conformément
              aux finalités décrites ci-dessus.
            </Label>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSubmit}
              disabled={!accepted || submitting}
              size="lg"
              className="gap-2 min-w-[160px]"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Enregistrement…
                </>
              ) : (
                "Continuer"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
